// Order-management constants, ported from the legacy Apps Script
// (eventHandlers.gs colour map, columnsAndViews.gs workshop view).
// Status strings are preserved EXACTLY (note the comma in "Invoice, not paid"
// and the parentheses in the refund states) so historic data stays consistent.

export const STATUS = {
  UNCUT: 'Uncut',
  CUT: 'Cut',
  SEWN: 'Sewn',
  COMPLETE: 'Complete',
  ON_HOLD: 'On hold',
  QUERY: 'Query',
  NEW_DESIGN: 'New Design',
  IN_PROGRESS: 'In progress',
  PENDING: 'Pending', // embroidery line: not yet embroidered (stage 0, like Uncut)
  EMBROIDERED: 'Embroidered', // embroidery line: done (stage 2, like Sewn)
  INVOICE_NOT_PAID: 'Invoice, not paid',
  REFUND_FULL: 'Refund (full)',
  REFUND_PARTIAL: 'Refund (partial)',
};

// Order matters: drives the filter-tab order and dropdown order.
export const STATUS_ORDER = [
  STATUS.UNCUT,
  STATUS.QUERY,
  STATUS.CUT,
  STATUS.SEWN,
  STATUS.IN_PROGRESS,
  STATUS.ON_HOLD,
  STATUS.NEW_DESIGN,
  STATUS.INVOICE_NOT_PAID,
  STATUS.COMPLETE,
  STATUS.REFUND_FULL,
  STATUS.REFUND_PARTIAL,
];

// Per-status row styling — verbatim from eventHandlers.gs onEdit `colors` map.
// `bg`/`fg` are hex; `strike` => strikethrough. Statuses absent here (e.g.
// "In progress") render with default styling, matching the legacy behaviour.
export const STATUS_STYLES = {
  [STATUS.UNCUT]: { bg: '#FFCFC9', fg: '#000000', strike: false },
  [STATUS.CUT]: { bg: '#FFE5A0', fg: '#000000', strike: false },
  [STATUS.ON_HOLD]: { bg: '#B10202', fg: '#FFFFFF', strike: false },
  [STATUS.SEWN]: { bg: '#B6D7A8', fg: '#000000', strike: false },
  [STATUS.COMPLETE]: { bg: '#E6E6E6', fg: '#000000', strike: false },
  [STATUS.QUERY]: { bg: '#E6CFF2', fg: '#000000', strike: false },
  [STATUS.NEW_DESIGN]: { bg: '#BFE1F6', fg: '#000000', strike: false },
  [STATUS.IN_PROGRESS]: { bg: '#FCE5CD', fg: '#000000', strike: false }, // partial — mixed line items
  [STATUS.PENDING]: { bg: '#FFCFC9', fg: '#000000', strike: false }, // = Uncut (not started)
  [STATUS.EMBROIDERED]: { bg: '#B6D7A8', fg: '#000000', strike: false }, // = Sewn (made)
  [STATUS.INVOICE_NOT_PAID]: { bg: '#B10202', fg: '#FFFFFF', strike: false },
  [STATUS.REFUND_FULL]: { bg: '#A0A0A0', fg: '#FFFFFF', strike: true },
  [STATUS.REFUND_PARTIAL]: { bg: '#A0A0A0', fg: '#FFFFFF', strike: false },
};

// Statuses considered "done" — excluded from the default active view
// (confirmed with the owner). Everything else is active/needs-work.
export const DONE_STATUSES = [STATUS.COMPLETE, STATUS.REFUND_FULL, STATUS.REFUND_PARTIAL];

export const isActiveStatus = (status) => !DONE_STATUSES.includes(status);

// ----- Two-tier status model -----
// Statuses a FELT line item (a sleeve) can hold — the making ladder + blockers.
export const LINE_ITEM_STATUS_ORDER = [
  STATUS.UNCUT,
  STATUS.CUT,
  STATUS.SEWN,
  STATUS.NEW_DESIGN,
  STATUS.ON_HOLD,
];

// Statuses an EMBROIDERY line item can hold (its own ladder + blocker).
export const EMBROIDERY_LINE_STATUS_ORDER = [STATUS.PENDING, STATUS.EMBROIDERED, STATUS.ON_HOLD];

// Detect an embroidery line by its product text (platforms list it as a line item).
export const isEmbroideryProduct = (text) => /embroider/i.test(String(text || ''));

/** The valid line-item statuses for a given line (felt vs embroidery). */
export const lineStatusOptions = (lineItem) =>
  lineItem?.isEmbroidery ? EMBROIDERY_LINE_STATUS_ORDER : LINE_ITEM_STATUS_ORDER;

// Making "stage" for the roll-up: 0 = not started, 1 = cut, 2 = made.
// Pending≈Uncut (0); Embroidered≈Sewn (2). New Design/On hold handled separately.
function makingStage(status) {
  if (status === STATUS.CUT) return 1;
  if (status === STATUS.SEWN || status === STATUS.EMBROIDERED) return 2;
  return 0; // Uncut, Pending, unknown
}

// Order-level statuses Kate can set MANUALLY on the header (override the roll-up).
export const HEADER_MANUAL_STATUS_ORDER = [
  STATUS.IN_PROGRESS,
  STATUS.COMPLETE,
  STATUS.QUERY,
  STATUS.ON_HOLD,
  STATUS.NEW_DESIGN,
  STATUS.INVOICE_NOT_PAID,
  STATUS.REFUND_FULL,
  STATUS.REFUND_PARTIAL,
];

/**
 * Roll up line-item statuses (felt + embroidery) to the header's derived status,
 * by making-stage. Precedence: any New Design > any On hold > all stage 0 (Uncut)
 * > all stage 2 (Sewn) > all stage 1 (Cut) > mixed (In progress). The header
 * keeps felt vocabulary; Pending≈Uncut and Embroidered≈Sewn.
 */
export function rollUpStatus(lineItems = []) {
  const list = (lineItems.length ? lineItems : [{ status: STATUS.UNCUT }]).map(
    (li) => li.status || STATUS.UNCUT
  );
  if (list.includes(STATUS.NEW_DESIGN)) return STATUS.NEW_DESIGN;
  if (list.includes(STATUS.ON_HOLD)) return STATUS.ON_HOLD;
  const stages = list.map(makingStage);
  if (stages.every((s) => s === 0)) return STATUS.UNCUT;
  if (stages.every((s) => s === 2)) return STATUS.SEWN;
  if (stages.every((s) => s === 1)) return STATUS.CUT;
  return STATUS.IN_PROGRESS;
}

/** Effective header status = manual override (if set) else the roll-up. */
export function effectiveStatus(manualStatus, lineItems) {
  return manualStatus || rollUpStatus(lineItems);
}

/**
 * True if a manual header status contradicts the line items (allowed, but flagged).
 * e.g. Complete while not all sewn, or a production status that isn't the roll-up.
 */
export function isHeaderInconsistent(manualStatus, lineItems) {
  if (!manualStatus) return false;
  const list = (lineItems || []).map((li) => li.status || STATUS.UNCUT);
  if (manualStatus === STATUS.COMPLETE) {
    // posted but not all made (Sewn felt / Embroidered embroidery)
    return !list.every((s) => s === STATUS.SEWN || s === STATUS.EMBROIDERED);
  }
  // A production-type manual status should match the actual roll-up.
  if ([STATUS.UNCUT, STATUS.CUT, STATUS.SEWN, STATUS.IN_PROGRESS, STATUS.NEW_DESIGN].includes(manualStatus)) {
    return manualStatus !== rollUpStatus(lineItems);
  }
  return false; // Query / On hold / Invoice / Refund are orthogonal — never "inconsistent"
}

// Workshop cutting filter defaults — the line-item statuses Kate cuts/needs.
export const WORKSHOP_LINE_STATUSES = [STATUS.UNCUT, STATUS.NEW_DESIGN];

// Legacy order-level workshop slice (kept for the column-hide preset/tab).
export const WORKSHOP_STATUSES = [STATUS.UNCUT, STATUS.QUERY];

// Sources (normalised) and their legacy "Channel" string.
export const SOURCE = {
  ETSY: 'etsy',
  EBAY: 'ebay',
  SHOPIFY: 'shopify',
  DIRECT: 'direct',
};

export const LEGACY_CHANNEL = {
  [SOURCE.ETSY]: 'Etsy',
  [SOURCE.EBAY]: 'Ebay',
  [SOURCE.SHOPIFY]: 'EG',
  [SOURCE.DIRECT]: 'Direct',
};

export const SOURCE_LABEL = {
  [SOURCE.ETSY]: 'Etsy',
  [SOURCE.EBAY]: 'eBay',
  [SOURCE.SHOPIFY]: 'Shopify',
  [SOURCE.DIRECT]: 'Direct',
};

// Columns hidden by the Workshop view (columnsAndViews.gs workshopViewOn).
// Expressed against the new grid's column field ids (see ordersColumns).
export const WORKSHOP_HIDDEN_FIELDS = [
  'orderedDate',
  'source',
  'platformOrderId2',
  'buyerUserId',
  'trackingNumber',
  'egNotes',
  'posted',
  'email',
  'address1',
  'address2',
  'townCity',
  'county',
  'postcode',
];

// Past-customer verdicts.
export const REPEAT_VERDICT = {
  YES: 'yes',
  NO: 'no',
  MAYBE: 'maybe',
};

export const ORDERS_COLLECTION = 'orders';
export const ORDERS_PAGE_SIZE = 50; // search result cap
// Live Active set: load the whole working set (it's only the not-done orders).
// High enough to cover normal operation; a banner warns if it is ever hit.
export const ACTIVE_LIMIT = 500;
// History/status tabs paginate in pages of this size ("Load more"). Sized for
// the full historical set (~28k orders) so each fetch is a meaningful chunk.
export const HISTORY_PAGE_SIZE = 250;
